/**
 * The one place an API date string becomes a Date.
 *
 * The API sends two kinds of value, and they must not be read the same way:
 *
 * - A calendar date, "2026-10-01" (payment date, due date, date of birth). It
 *   names a day in the group's books and is the same day for every viewer, so
 *   it is built from its parts as local midnight and never converted.
 * - An instant, "2026-09-30T21:00:00.000000Z" (createdAt, paidAt, expiresAt).
 *   It is a moment in time and is shown on the viewer's own clock.
 *
 * `new Date("2026-10-01")` reads a calendar date as UTC midnight, which is the
 * evening of 30 Sep anywhere west of UTC. dates.test.ts fails for any file that
 * calls `new Date(value)` on its own instead of going through here.
 */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isCalendarDate(value: string): boolean {
  return CALENDAR_DATE.test(value);
}

export function parseApiDate(value: string): Date {
  const parts = CALENDAR_DATE.exec(value);
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return new Date(value);
}

/** An instant on the viewer's clock, naming the zone (e.g. "Oct 13, 2026, 11:30 AM GMT+3"),
 *  so two members in different places can tell why their times differ. */
export function formatTimestamp(value: string): string {
  return parseApiDate(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}
